import type { ExactLocaleConfig } from "@vuepress/helper/client";
import { isArray, isString, useLocaleConfig } from "@vuepress/helper/client";
import type {
  DASHNamespaceLoader,
  DefaultLayoutProps,
  HLSConstructorLoader,
  PlayerSrc,
  TextTrackInit,
} from "vidstack";
import type { MediaPlayerElement } from "vidstack/elements";
import type { VidstackPlayerConfig } from "vidstack/global/player";
import type { PropType, VNode } from "vue";
import { defineComponent, h, onBeforeUnmount, onMounted, shallowRef } from "vue";

import type { VidstackLocaleData } from "../../shared/index.js";
import { getLink } from "../utils/getLink.js";

import "vidstack/player/styles/default/theme.css";
import "vidstack/player/styles/default/layouts/audio.css";
import "vidstack/player/styles/default/layouts/video.css";
import "../styles/vidstack.scss";

declare const DASHJS_INSTALLED: boolean;
declare const HLS_JS_INSTALLED: boolean;
// oxlint-disable-next-line typescript/no-unnecessary-type-arguments
declare const VIDSTACK_LOCALES: ExactLocaleConfig<VidstackLocaleData>;

export default defineComponent({
  name: "VidStack",

  props: {
    /**
     * sources
     */
    src: {
      type: [String, Array, Object] as PropType<PlayerSrc>,
      required: true,
    },

    /**
     * tracks
     */
    tracks: {
      type: Array as PropType<TextTrackInit[]>,
      default: () => [],
    },

    /**
     * poster
     */
    poster: String,

    /**
     * thumbnails
     */
    thumbnails: String,

    /**
     * title
     */
    title: String,

    /**
     * VidStack player options
     */
    player: {
      type: Object as PropType<
        Omit<VidstackPlayerConfig, "target" | "src" | "sources" | "tracks" | "title" | "poster">
      >,
    },

    /**
     * VidStack layout options
     */
    layout: {
      type: Object as PropType<Partial<DefaultLayoutProps>>,
    },

    /**
     * Dark mode
     */
    darkmode: Boolean,
  },

  setup(props) {
    const vidstack = shallowRef<HTMLElement>();
    const locale = useLocaleConfig(VIDSTACK_LOCALES);

    let player: MediaPlayerElement;

    onMounted(async () => {
      if (__VUEPRESS_SSR__) return;

      const { VidstackPlayer, VidstackPlayerLayout } = await import("vidstack/global/player");

      const options: VidstackPlayerConfig = {
        // oxlint-disable-next-line typescript/no-non-null-assertion
        target: vidstack.value!,
        crossOrigin: true,
        poster: props.poster,
        title: props.title,
        ...props.player,
        src: isString(props.src)
          ? getLink(props.src)
          : isArray(props.src)
            ? props.src.map((src) => (isString(src) ? getLink(src) : src))
            : props.src,
        layout: new VidstackPlayerLayout({
          colorScheme: props.darkmode ? "dark" : "light",
          thumbnails: props.thumbnails,
          translations: locale.value,
          ...props.layout,
        }),
      };

      if (props.tracks.length > 0) options.tracks = props.tracks;

      // oxlint-disable-next-line typescript/no-non-null-assertion
      player = (await VidstackPlayer.create(options))!;

      player.addEventListener("provider-change", () => {
        if (player.provider?.type === "hls" && HLS_JS_INSTALLED) {
          player.provider.library = (() =>
            import(/* webpackChunkName: "hls" */ "hls.js/dist/hls.min.js")) as HLSConstructorLoader;
        } else if (player.provider?.type === "dash" && DASHJS_INSTALLED) {
          player.provider.library = (() =>
            import(/* webpackChunkName: "dashjs" */ "dashjs")) as DASHNamespaceLoader;
        }
      });
    });

    onBeforeUnmount(() => {
      try {
        player.destroy();
      } catch {
        // Do nothing
      }
    });

    return (): VNode => h("div", { ref: vidstack });
  },
});
